import { useEffect, useMemo, useState } from "react";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import useAggregationExtended from "@Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";

// MUI
import { Box, Stack, SxProps } from "@mui/material";
import { getGridStringOperators, GridCellParams, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";

// Types
import { IFinanceDeal } from "@/DataPages/types/financeDeals/model";

// Helpers
import { generateSx, getPinnedCellClassName, getRowClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { notEqualsOperator } from "@Layouts/components/Tables/DataSmartTable/helpers/custom-filters.ts";

// Components
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { COLUMN_RESIZE_FACTOR } from "@/Constants/tables";
import { tableKeys } from "@/Constants/tableKeys";
import { DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@/Washouts/constants";

const HEADER_CLASS_NAME = "!text-[12px] table-header-top-aligned show-as-column font-montserrat font-semibold  text-default";
const CELL_CLASS_NAME = "table-small-cell";
const FOOTER_CLASS_NAME = "auto-generated-group-footer-root";
const TEXT_ALIGN_RIGHT = " text-right";

const ONE_HUNDRED_THIRTY = 130;
const pinnedColumns = { left: ["date", "age", "dealNumber", "financeEmployeeName", "dealStatus", "__empty"] };

function getColumns(timezone: string): GridColDef[] {
	return [
		{
			field: "_id",
			headerName: "ID",
			width: 10,
			filterable: false,
		},
		{
			field: "date",
			headerName: "Sale Date",
			type: "date",
			width: 80,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
			valueGetter: (value) => value && getDateInUTC(value),
			renderCell: (params) => {
				const valueToShow = isFooterCell(params) ? "Total" : getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded);
				return (
					<TypographyCell
						render="absolute"
						params={params}
						issues={[]}
						hints={{ isFunded: false }}
						fontSize={isFooterCell(params) ? "14px" : "12px"}
					>
						{valueToShow}
					</TypographyCell>
				);
			},
		},
		{
			field: "age",
			headerName: "Age",
			width: 60,
			type: "number",
			sortable: true,
			headerClassName: HEADER_CLASS_NAME + TEXT_ALIGN_RIGHT,
			cellClassName: (params) => getPinnedCellClassName(params, CELL_CLASS_NAME),
			renderCell: (params) => {
				return (
					<TypographyCell
						render="absolute"
						params={params}
						issues={[]}
						hints={{ isFunded: false }}
						fontSize={isFooterCell(params) ? "14px" : "12px"}
						formatOptions={{ decimals: isFooterCell(params) ? 1 : 0 }}
					/>
				);
			},
		},
		{
			field: "dealNumber",
			headerName: "Deal #",
			minWidth: 80,
			width: 80,
			align: "right",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME + TEXT_ALIGN_RIGHT,
			cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
			renderCell: (params) => {
				return (
					<TypographyCell
						params={params}
						issues={[]}
						hints={{ isFunded: false }}
						fontSize={isFooterCell(params) ? "14px" : "12px"}
					/>
				);
			},
		},
		{
			field: "financeEmployeeName",
			headerName: "Finance Manager",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
			valueGetter: (_value, row) => {
				const financeEmployeeId = row?.financeEmployeeId === "9999" ? "HOUSE" : row?.financeEmployeeId;
				return row?.financeEmployeeName ?? financeEmployeeId;
			},
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return "";
				}
				return <CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? 130} fontSize="12px" />;
			},
		},
		{
			field: "dealStatus",
			headerName: "STAT",
			minWidth: 50,
			width: 50,
			headerClassName: HEADER_CLASS_NAME,
			resizable: false,
			filterOperators: [
				...getGridStringOperators(), // :contentReference[oaicite:0]{index=0}
				// then our custom “Not equal”
				notEqualsOperator,
			],
			cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
			renderCell: (param) => {
				if (!param.value) {
					return null;
				}
				return (
					<Stack display={"flex"} direction={"row"} justifyContent={"start"}>
						<Box>
							<CircleRoundedIcon
								className={`deal-status-${param.value}`}
								sx={{
									fontSize: "12px",
									marginTop: "-3px",
									marginRight: "5px",
								}}
							/>
						</Box>
						<CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 50} fontSize="12px" />
					</Stack>
				);
			},
		},
		{
			field: "__empty",
			headerName: "",
			sortable: false,
			resizable: false,
			filterable: false,
			minWidth: 12,
			width: 12,
			maxWidth: 12,
			headerClassName: "!z-10 !bg-white !border-none !p-0",
			cellClassName: () => "empty-row !bg-white !border-none !p-0",
			renderCell: () => <TypographyCell className={"!bg-blue"} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			minWidth: 70,
			width: 70,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 70} fontSize="12px" />;
			},
		},
		{
			field: "customerNumber",
			headerName: "Customer #",
			minWidth: 80,
			width: 100,
			resizable: false,
			align: "right",
			headerClassName: HEADER_CLASS_NAME + TEXT_ALIGN_RIGHT,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 80} fontSize="12px" />;
			},
		},
		{
			field: "customerName",
			headerName: "Customer Name",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 120} fontSize="12px" />;
			},
		},
		{
			field: "vinNumber",
			headerName: "VIN",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			headerClassName: HEADER_CLASS_NAME,
			resizable: false,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 130} fontSize="12px" />;
			},
		},
		{
			field: "lender",
			headerName: "Lender",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 110} fontSize="12px" />;
			},
		},
		{
			field: "amountFinanced",
			headerName: "Financed Amount",
			minWidth: 140,
			width: 140,
			align: "right",
			type: "number",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => (
				<TypographyCell
					render="absolute"
					params={params}
					issues={[]}
					hints={{ isFunded: false, isMoney: true }}
					fontSize={isFooterCell(params) ? "14px" : "12px"}
				/>
			),
		},
		{
			field: "salesOneEmployeeName",
			headerName: "Sales Associate",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => {
				let value = params.row.salesOneEmployeeName || params.row.salesOneEmployeeId;
				if (params.row.salesOneEmployeeId === "9999") {
					value = "HOUSE";
				}
				return <CustomCell value={value} hasTooltip maxWith={params.colDef.width ?? 120} fontSize="12px" />;
			},
		},
		{
			field: "tradeACV",
			headerName: "T-ACVTRADE",
			minWidth: 100,
			width: 115,
			type: "number",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			align: "right",
			cellClassName: getCellClassNameFn([], { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return "";
				}
				return (
					<TypographyCell
						render="absolute"
						params={params}
						issues={[]}
						hints={{ isFunded: false, isMoney: true }}
						fontSize={"12px"}
					/>
				);
			},
		},
		{
			field: "tradeNet",
			headerName: "T-NETTRADE",
			minWidth: 100,
			width: 115,
			align: "right",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return "";
				}
				return (
					<TypographyCell
						render="absolute"
						params={params}
						issues={[]}
						hints={{ isFunded: false, isMoney: true }}
						fontSize={"12px"}
					/>
				);
			},
		},
		{
			field: "closeDate",
			headerName: "CLOSE-DATE",
			width: 80,
			resizable: false,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueGetter: (value) => getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell />;
				}
				const valueToShow = getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) ?? "";
				return <CustomCell value={valueToShow} hasTooltip maxWith={params.colDef.width ?? 90} fontSize="12px" />;
			},
		},
		{
			field: "statusDate",
			headerName: "STAT-DATE",
			width: 125,
			resizable: false,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueFormatter: (value) => (value ? getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12hPadded) : ""),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell />;
				}
				const valueToShow = getFormattedDate(params.value, timezone, DATE_FORMAT.fullAbbrTime12hPadded) ?? "";
				return <CustomCell value={valueToShow} hasTooltip maxWith={params.colDef.width ?? 90} fontSize="12px" />;
			},
		},
		{
			field: "activeStatusDV",
			headerName: "ACTIVE-STAT-DV",
			minWidth: 100,
			width: 100,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 100} fontSize="12px" />;
			},
		},
		{
			field: "statusCodeDV",
			headerName: "STAT-CODE-DV",
			minWidth: 100,
			width: 100,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 100} fontSize="12px" />;
			},
		},
		{
			field: "vstCode",
			headerName: "VST-CODE",
			minWidth: 80,
			width: 80,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 80} fontSize="12px" />;
			},
		},
		{
			field: "offsetStatusDate",
			headerName: "OFFSET-STAT-DATE-CAT",
			minWidth: 110,
			width: 110,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 110} fontSize="12px" />;
			},
		},
		{
			field: "dealOriginalDate",
			headerName: "DEAL-ORIGIN-DATE",
			minWidth: 110,
			width: 110,
			resizable: false,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueGetter: (value) => getDateInUTC(value),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell />;
				}
				const valueToShow = getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) ?? "";
				return <CustomCell value={valueToShow} hasTooltip maxWith={params.colDef.width ?? 110} fontSize="12px" />;
			},
		},
		{
			field: "dealStartDate",
			headerName: "DEAL-START-DATE",
			minWidth: 100,
			width: 100,
			resizable: false,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueGetter: (value) => getDateInUTC(value),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell />;
				}
				const valueToShow = getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) ?? "";
				return <CustomCell value={valueToShow} hasTooltip maxWith={params.colDef.width ?? 100} fontSize="12px" />;
			},
		},
		{
			field: "dealType",
			headerName: "Deal Type",
			minWidth: 80,
			width: 80,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (param) => {
				return <CustomCell value={param.value} hasTooltip maxWith={param.colDef.width ?? 80} fontSize="12px" />;
			},
		},
		{
			field: "financeReserve",
			headerName: "FIN-RES",
			minWidth: 80,
			width: 80,
			align: "right",
			type: "number",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => {
				const isFooter = params.id === FOOTER_CLASS_NAME;
				if (isFooter) {
					return "";
				}
				return (
					<TypographyCell
						render="absolute"
						params={params}
						issues={[]}
						hints={{ isFunded: false, isMoney: true }}
						fontSize={isFooterCell(params) ? "14px" : "12px"}
					/>
				);
			},
		},
	];
}

const getCellClassName = (params: GridCellParams) => {
	// Targeting footer
	const isFooter = params.id === "auto-generated-group-footer-root";
	if (!isFooter) {
		return "";
	}

	// Use this to change the cell class name depending on the cell value
	const negatives = ["amountFinanced"];
	if (negatives.includes(params.field) && (params.value as number) < 0) {
		return "aggregation-footer-negative";
	}

	// Append label 'Total' to the cell value in footer
	if (params.field === "date") {
		return "aggregation-footer-total";
	}

	if (params.field === "dealNumber") {
		return "aggregation-footer-negative";
	}

	return "";
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
};

interface IFinanceDealsTableProps {
	data: IFinanceDeal[];
}

export default function FinanceDealsTable(props: Readonly<IFinanceDealsTableProps>) {
	const { data } = props;
	const apiRef = useGridApiRef();

	const { timezone } = useLocation();

	const aggregations = useAggregationExtended({
		uniqueCount: true,
		conditionalSum: { title: "Trades: ", condition: 0, operation: "NOT EQUAL" },
	});

	const initialState = {
		aggregation: {
			model: {
				amountFinanced: "sum",
				dealNumber: "uniqueCount",
				tradeACV: "conditionalSum",
				age: "avg",
			},
		},
	};

	const slotProps = useMemo(
		() => ({
			toolbar: {
				titleExpandible: {
					className: "mt-[8px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
					hide: true,
				},
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
			},
			noRowsOverlay: {
				title: "No Finance Deal Data",
			},
		}),
		[data]
	);

	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		setSx(generateSx(apiRef));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [props, apiRef]);

	const columns = getColumns(timezone);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.FNI_MANAGEMENT_FI_DEAL,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={props.data}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			rowHeight={DEFAULT_ROW_HEIGHT}
			maxRows={19}
			pinnedColumns={pinnedColumns}
			getRowClassName={getRowClassName}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={slots}
			slotProps={slotProps}
			hideFooter
			initialState={initialState}
			getCellClassName={getCellClassName}
			customProps={{
				tableContainerClassName: "h-full w-full [&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
				tableClassName: {
					"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
					"[&_.MuiDataGrid-row:not(.footer,.empty-row):hover_.MuiDataGrid-cell:not(.footer,.empty-row)]:shadow-table-hov": true,
				},
				aggregationFooterClassName: "aggregation-footer regular-footer",
				customAggregationsFn: { ...aggregations },
				expandableRows: true,
				hide: false,
			}}
			sx={sx}
		/>
	);
}
